/**
 * OfflineBanner — a slim status bar that surfaces connection / sync state:
 *   • offline           → "غير متصل — يتم عرض آخر بيانات محفوظة" (+ pending count)
 *   • online + syncing  → "جاري رفع العمليات المحفوظة..."
 *   • online + pending  → "N عملية في انتظار الرفع" (tap to retry)
 * Hidden when online with nothing queued.
 */
import { WifiOff, UploadCloud, Loader2 } from 'lucide-react';
import { useOnlineStatus } from '@/lib/useOnlineStatus';
import { useSyncStatus } from '@/lib/useSyncStatus';
import { syncNow } from '@/lib/syncEngine';

export default function OfflineBanner() {
  const online = useOnlineStatus();
  const { pending, syncing } = useSyncStatus();

  if (online && pending === 0 && !syncing) return null;

  // Offline
  if (!online) {
    return (
      <div className="no-print flex items-center justify-center gap-2 bg-amber-500 text-white text-xs font-semibold px-3 py-1.5" dir="rtl">
        <WifiOff className="w-3.5 h-3.5" />
        <span>
          غير متصل بالإنترنت — يتم عرض آخر بيانات محفوظة
          {pending > 0 ? ` • ${pending} عملية محفوظة للرفع` : ''}
        </span>
      </div>
    );
  }

  // Online + syncing
  if (syncing) {
    return (
      <div className="no-print flex items-center justify-center gap-2 bg-sky-600 text-white text-xs font-semibold px-3 py-1.5" dir="rtl">
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        <span>جاري رفع العمليات المحفوظة...</span>
      </div>
    );
  }

  // Online + pending (idle) — tappable to retry
  return (
    <button
      onClick={() => syncNow()}
      className="no-print w-full flex items-center justify-center gap-2 bg-sky-600 text-white text-xs font-semibold px-3 py-1.5"
      dir="rtl"
    >
      <UploadCloud className="w-3.5 h-3.5" />
      <span>{pending} عملية في انتظار الرفع — اضغط للمحاولة</span>
    </button>
  );
}
